import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { GoCheckCircleFill } from "react-icons/go";
import { MdLocationOn } from "react-icons/md";
import { checkResultComplete } from "services/resultsRequests";
import { Module, Project, MyLearningJourneyQuiz } from "utils/interfaces";
import { externalNavigate, removeItem } from "utils/utils";
import { AnimatePresence, motion } from "framer-motion";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface JourneyBadgeProps {
  ref?: any;
  index: number;
  type: "projects" | "modules" | "quizzes";
  lesson: Project | Module | MyLearningJourneyQuiz;
  toComplete: boolean;
  setUncompletedIndexes: React.Dispatch<React.SetStateAction<number[]>>;
  setShowModulesPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}

const JourneyBadge: React.FC<JourneyBadgeProps> = forwardRef(
  (
    {
      index,
      type,
      lesson,
      toComplete,
      setUncompletedIndexes,
      setShowModulesPopUp,
    },
    ref,
  ) => {
    // Status definition
    const [completed, setCompleted] = useState(false);
    const [hovering, setHovering] = useState(false);

    useImperativeHandle(ref, () => ({
      handleClickBadge() {
        handleClickBadge();
      },
    }));

    /**
     * Function used to define if a lesson is completed
     */
    useEffect(() => {
      if (lesson._id) {
        checkResultComplete(type, lesson._id).then((response: boolean) => {
          // Set the current state
          setCompleted(response);
        });
      }
    }, [lesson]);

    /**
     * Function used when clicking on a badge
     */
    const handleClickBadge = () => {
      // If the project contains modules, then show a little pop up with
      // the projects inside that class
      if (
        type === "projects" &&
        (lesson as Project).modules &&
        (lesson as Project).modules.length > 0
      ) {
        setShowModulesPopUp(true);
      } else if (type === "quizzes") {
        externalNavigate(`/quiz/${lesson._id}`);
      } else {
        externalNavigate(`/editor/${type}/${lesson._id}`);
      }
    };

    /**
     * Function used to update the uncompletedProjects list
     */
    useEffect(() => {
      // Remove from the list if completed
      if (completed) {
        setUncompletedIndexes((currentList) => removeItem(currentList, index));
      }
      // Otherwise add it to the list
      else {
        setUncompletedIndexes((currentList) => [
          ...new Set([...currentList, index]),
        ]);
      }
    }, [completed]);

    return (
      <div
        className={`${
          type === "modules" ? "scale-[0.9] " : ""
        }w-[103px] h-full flex-col justify-center items-center gap-2 inline-flex relative cursor-pointer border border-transparent hover:backdrop-blur-sm ${
          completed
            ? "hover:border-success-500 hover:bg-success-200/20"
            : toComplete
              ? "hover:border-warning-500 hover:bg-warning-200/20"
              : "hover:border-neutral-300 hover:bg-neutral-300/20"
        } ease-in-out duration-150 rounded-lg`}
        onClick={handleClickBadge}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div className="w-20 h-20 rounded-full relative overflow-hidden">
          {completed && (
            <div className="w-full h-full absolute rounded-full bg-neutral-50/70 backdrop-blur-sm justify-center items-center inline-flex z-10">
              <GoCheckCircleFill className="w-8 h-8 fill-success-700" />
            </div>
          )}
          <ImageSkeleton
            src={`/api/imgs/${type}/${lesson._id}/badgeImage`}
            alt="badge"
            className="w-full h-full rounded-full select-none relative"
          />
        </div>
        <div className="grow shrink text-center text-neutral-500 text-[10px] font-semibold font-sans leading-3 tracking-tight overflow-hidden select-none">
          {lesson.title}
        </div>
        <AnimatePresence>
          {toComplete && !hovering && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
              className={"absolute -right-1 -top-2.5"}
            >
              <MdLocationOn className="w-8 h-8 fill-warning-500" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export default JourneyBadge;
